import { Amount, DocumentViewer, ProviderClaimPayment, ProviderDocumentUrl, renderAmount, Spinner } from '@enter/core';
import { format } from 'date-fns';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { IObservedRootStore } from 'stores';
import * as S from './claim-timeline-table.styles';

interface IPaymentDetailProps extends IObservedRootStore {
    payment: ProviderClaimPayment;
}

// Reusable row components
const TextRow = React.memo<{
    label?: string;
    value: string | number | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{value}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const DateRow = React.memo<{
    label?: string;
    value: string | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    const date = format(value, 'MMM dd, yyyy');

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{date}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const AmountRow = React.memo<{
    label?: string;
    value: Amount | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{renderAmount(value)}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const Details: React.FunctionComponent<{ payment: ProviderClaimPayment }> = React.memo(({ payment }) => {
    return (
        <S.ExpandContainer>
            <S.ExpandTable>
                <S.ExpandTBody>
                    <TextRow label="ID" value={payment.id} />
                    <DateRow label="Payment Date" value={payment.paymentDate} />
                    <DateRow label="Received Date" value={payment.receivedDate} />
                    <TextRow label="Memo" value={payment.memo} />
                    <TextRow label="Payer Control Number" value={payment.payerControlNumber} />
                    <TextRow label="Status" value={payment.status} />
                    <AmountRow label="Charge Amount" value={payment.chargeAmount} />
                    <AmountRow label="Allowed Amount" value={payment.allowedAmount} />
                    <AmountRow label="Payer Amount" value={payment.payerAmount} />
                    <AmountRow label="Patient Amount" value={payment.patientAmount} />
                </S.ExpandTBody>
            </S.ExpandTable>
        </S.ExpandContainer>
    );
});

export const PaymentDetail: React.FunctionComponent<IPaymentDetailProps> = inject('rootStore')(
    observer((props: IPaymentDetailProps) => {
        const { payment, rootStore } = props;
        if (!rootStore) {
            return <></>;
        }

        const [loading, setLoading] = useState(false);
        const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();

        useEffect(() => {
            (async (): Promise<void> => {
                if (!payment.id) {
                    return;
                }

                setLoading(true);

                const url = await rootStore.claimStore.loadPaymentDownloadUrl(payment.id);
                setDocumentUrl(url);

                setLoading(false);
            })();
        }, [payment]);

        const renderDocument = () => {
            if (documentUrl?.url) {
                return <DocumentViewer url={documentUrl.url} />;
            }
        };

        return (
            <S.ExpandWrap key={payment.id}>
                {loading ? (
                    <Spinner />
                ) : (
                    <S.ExpandContainer>
                        <Details payment={payment} />
                        {renderDocument()}
                    </S.ExpandContainer>
                )}
            </S.ExpandWrap>
        );
    }),
);
